import React from 'react';
import DOMPurify from 'dompurify';

// Sanitize-and-memoize wrapper for rich HTML pumped into innerHTML.
//
// Two jobs:
// 1. Strip dangerous markup. convertADFToHTML in JiraDashboard concatenates
//    raw strings from Jira (text, link hrefs, code-block languages, etc.)
//    with no escaping — a malicious commenter could inject <script> or
//    javascript: URLs. DOMPurify removes script tags, event handlers, and
//    unsafe URL schemes by default. SECURITY_AUDIT C-1.
// 2. Stop React from re-setting innerHTML on every parent render. React
//    compares the dangerouslySetInnerHTML prop by object identity, so a
//    fresh `{__html: ...}` literal on each render destroys the inner DOM
//    (including <img> elements that then remount and flash). Memoizing the
//    object by the sanitized string keeps the reference stable.

// Whitelist tailored for ADF-rendered content. Add tags here if Jira ADF
// gains new output that we legitimately need; never add `script`,
// `iframe`, `object`, `embed`, or `style`.
const PURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'a', 'b', 'i', 'u', 's', 'em', 'strong', 'code', 'pre', 'br', 'hr',
    'p', 'div', 'span', 'blockquote',
    'ul', 'ol', 'li',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'table', 'thead', 'tbody', 'tr', 'td', 'th',
    'img'
  ],
  ALLOWED_ATTR: [
    'href', 'target', 'rel', 'title', 'alt',
    'src', 'width', 'height', 'loading',
    'class', 'style'
  ],
  // Only allow safe URL schemes — blocks javascript:, data:, vbscript:, etc.
  // Mirrors the dgai-pwa/src/utils/safeUrl.js allowlist. The `\/(?!\/)` is
  // load-bearing: a bare leading `/` is a same-origin path (fine), but `//…`
  // is a protocol-relative URL that the browser resolves to `https://…` —
  // i.e. an off-host link that would otherwise sneak past this backstop.
  ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|\/(?!\/)|#)/i,
  ADD_ATTR: ['target'],  // permit target="_blank" we add for media links
  RETURN_TRUSTED_TYPE: false,
};

// Force every `target="_blank"` link to also carry `rel="noopener noreferrer"`
// so a malicious link can't break the tab opener relationship.
DOMPurify.addHook('afterSanitizeAttributes', (node) => {
  if (node.tagName === 'A' && node.getAttribute('target') === '_blank') {
    node.setAttribute('rel', 'noopener noreferrer');
  }
});

const RichTextHtml = React.memo(function RichTextHtml({ html, className, style }) {
  const dangerously = React.useMemo(
    () => ({ __html: DOMPurify.sanitize(html || '', PURIFY_CONFIG) }),
    [html]
  );
  return <div className={className} style={style} dangerouslySetInnerHTML={dangerously} />;
});

export default RichTextHtml;
